/**
 * TA Cockpit — Role Prioritisation tab.
 *
 * Two sub-tabs, named for WHO READS THEM rather than for what they contain (Theresa, Sep 25 2026):
 *
 *   Company View    for any business stakeholder: how their open roles are rated, what each rating
 *                   gets them, and how much capacity the TA team has for the lot
 *   Cluster View    for the cluster leads' weekly: which cluster to help first, the capacity
 *                   what-ifs, and the board where support between clusters is actually cut
 *
 * They were "Prioritisation score" and "Cluster Lead Check-In", which named our own artefacts —
 * nobody outside TA knows what a prioritisation score is, and the people who do know went to the
 * wrong one of the two. The rename came with the content moving to match: the whole-company role
 * distribution went to the Company View, Balance the load came the other way.
 *
 * ⚠️ THE SLUGS ARE UNCHANGED (`score` / `checkin`). They are in pasted links and in the usage
 * rollups, which group by path — renaming a label is free, renaming a URL is not.
 *
 * The sub-tab is driven by the URL (`/ta-cockpit/role-prioritisation/:subtab`) so a colleague can
 * paste a link that lands on the checkin view directly. An unknown or missing slug resolves to the
 * default (score), the parent view handles that via one nav call rather than a redirect loop.
 */
import { Navigate, useNavigate } from 'react-router-dom';
import { logAnalyticsEvent } from '../../../../../analytics/nativeAnalytics';
import { CockpitTabs } from '../cockpitTabs';
import { CheckInTab } from '../checkin/CheckInTab';
import { PrioritisationScore } from './PrioritisationScore';

type SubTab = 'score' | 'checkin';

const SUB_TABS = [
  { value: 'score', label: 'Company View' },
  { value: 'checkin', label: 'Cluster View' },
] as const satisfies readonly { value: SubTab; label: string }[];

const DEFAULT_SUB: SubTab = 'score';
const SUB_VALUES = new Set<string>(SUB_TABS.map((s) => s.value));

export function RolePrioritisationTab({
  userEmail,
  subtabSlug,
}: {
  userEmail?: string | null;
  subtabSlug?: string;
}) {
  const navigate = useNavigate();

  /** Same canonical redirect as Tracking — see the note there; the usage rollups group by path. */
  if (!subtabSlug || !SUB_VALUES.has(subtabSlug)) {
    return <Navigate to={`/ta-cockpit/role-prioritisation/${DEFAULT_SUB}`} replace />;
  }
  const sub = subtabSlug as SubTab;

  function onSubChange(next: SubTab) {
    void logAnalyticsEvent('ta_cockpit_subtab_changed', {
      tab: 'role-prioritisation',
      subtab: next,
    });
    navigate(`/ta-cockpit/role-prioritisation/${next}`);
  }

  return (
    <div className="space-y-4">
      <CockpitTabs
        size="sub"
        value={sub}
        tabs={SUB_TABS}
        onChange={onSubChange}
        ariaLabel="Role Prioritisation sections"
      />

      {sub === 'score' ? <PrioritisationScore /> : <CheckInTab userEmail={userEmail} />}
    </div>
  );
}
